import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { HiOutlineArrowLeft, HiRefresh } from 'react-icons/hi';
import { IoSearch, IoClose } from 'react-icons/io5';
import moment from 'moment';

import { customerService } from '../../services/customer.service';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import LogicContext from '../../Context/LogicContext';
import { SetCorporateEdit } from '../../store/corporateAtom';

import RatingScroll from './RatingScroll';
import RatingRangeSlider from './RatingSlider';
import GeneralCustomTable from '../../helpers/GeneralCustomTable';
import { formatCustomDate } from '../../helpers/formatCustomDate';

export default function RatingDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);
  const setEdit = SetCorporateEdit();

  const [ratingList, setRatingList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [range, setRange] = useState([1, 5]);
  const [loading, setLoading] = useState(false);

  const currModuleId = pathname.split('/')[1];
  const currModuleName = pathname.split('/')[2];
  const currUserMenu = pathname.split('/')[3];

  const myPromiseModal = createModal(GeneralModal);

  useEffect(() => {
    document.title = 'Customer Ratings | Details';
    fetchRatings();
  }, [id]);

  const fetchRatings = async () => {
    setLoading(true);
    try {
      const response = await customerService.getRatingDetails(id, ...range);
      if (response.data?.length) {
        setRatingList(response.data);
        setFilteredList(response.data);
      } else {
        setRatingList([]);
        setFilteredList([]);
      }
    } catch (err) {
      logicContext.showToast('Failed to load ratings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onClearInput = () => {
    setSearchText('');
    setFilteredList(ratingList);
  };

  const handleSearch = text => {
    setSearchText(text);
    if (text.trim() === '') {
      setFilteredList(ratingList);
      return;
    }
    const searchTerm = text.toLowerCase();
    const filtered = ratingList.filter(item => Object.values(item).some(value => value?.toString().toLowerCase().includes(searchTerm)));
    setFilteredList(filtered);
  };

  const goBack = () => navigate(-1);

  const sendRatingReply = async rating => {
    const result = await myPromiseModal({
      title: `Reply to ${rating?.Customer_First_Name || 'Customer'}`,
      body: rating?.Rating_Remark || 'No remark provided',
      showInput: true,
      existMessage: rating?.Rating_Feedback || '',
      placeHolder: 'Enter your reply...',
    });

    if (!result?.decision) return;
    if (!result.message?.trim()) {
      return logicContext.showToast('Reply cannot be empty', 'warning');
    }

    let bodyData = {};
    switch (id) {
      case '1':
        bodyData = { sql_ref_no: 103, ref_no: rating.rating_ref_no, rating_remark: result.message, option: 5 };
        break;
      case '2': // Shopping
        bodyData = { sql_ref_no: 104, salesorder_id: rating.rating_ref_no, rating_remark: result.message, option: 3 };
        break;
      case '3': // Shipping
        bodyData = { sql_ref_no: 103, ref_no: rating.rating_ref_no, rating_remark: result.message, option: 4 };
        break;
      case '4': // Airtime
      case '5':
      case '6':
        bodyData = { sql_ref_no: 104, ref_no: rating.rating_ref_no, rating_remark: result.message, option: 3 };
        break;
      default:
        return;
    }

    try {
      const res = await customerService.submitReplyToRating(bodyData);
      const data = logicContext.httpResponseManager(res.data[0]);
      if (data?.message_id === 1) {
        logicContext.showToast(data.message_text, 'success');
        fetchRatings();
      }
    } catch (err) {
      logicContext.showToast('Failed to submit reply', 'error');
    }
  };

  const columns = [
    {
      title: 'Ref No',
      dataIndex: 'rating_ref_no',
      key: 'rating_ref_no',
      sorter: (a, b) => (a?.rating_ref_no ?? '').localeCompare(b?.rating_ref_no ?? ''),
      width: 140,
    },
    {
      title: 'Date',
      dataIndex: 'Rating_Date',
      key: 'Rating_Date',
      sorter: (a, b) => (a?.Rating_Date ?? '').localeCompare(b?.Rating_Date ?? ''),
      render: (_, record) => <span className="text-gray-700">{formatCustomDate(record.Rating_Date)}</span>,
      width: 160,
    },
    {
      title: 'Customer',
      dataIndex: 'Customer_Full_Name',
      key: 'Customer_Full_Name',
      sorter: (a, b) => (a?.Customer_Full_Name ?? '').localeCompare(b?.Customer_Full_Name ?? ''),
      render: (_, record) => <div className="font-medium text-gray-900">{record.Customer_Full_Name}</div>,
    },
    {
      title: 'Mobile',
      dataIndex: 'Customer_Mobile_No',
      key: 'Customer_Mobile_No',
      width: 140,
    },
    {
      title: 'Email',
      dataIndex: 'Customer_Email_Address',
      key: 'Customer_Email_Address',
    },
    {
      title: 'Rating',
      dataIndex: 'Rating_Value',
      key: 'Rating_Value',
      sorter: (a, b) => (a?.Rating_Value ?? 0) - (b?.Rating_Value ?? 0),
      render: (_, record) => <span className="font-semibold text-amber-600">{record.Rating_Value} / 5</span>,
      width: 100,
    },
    {
      title: 'Remark',
      dataIndex: 'Rating_Remark',
      key: 'Rating_Remark',
      ellipsis: true,
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <button
          onClick={() => sendRatingReply(record)}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-indigo-700"
        >
          Reply
        </button>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={goBack} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                <HiOutlineArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Customer Ratings</h1>
              <span className="font-mono text-sm text-gray-500">Type: {id}</span>
            </div>

            <button
              onClick={fetchRatings}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200"
            >
              <HiRefresh size={18} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl pt-6">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Sidebar - Rating Types / List */}
          <div className="flex-shrink-0 lg:w-80 xl:w-96">
            <div className="sticky top-20 overflow-hidden rounded-xl bg-white">
              <RatingScroll viewItemDetails={newId => navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${newId}`)} />
            </div>
          </div>

          {/* Main Content */}
          <div className="min-w-0 flex-1">
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              {/* Filters */}
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b pb-5">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="md:mb-3">
                    <RatingRangeSlider handlerRatingChange={setRange} initialValue={range} />
                  </div>

                  <button
                    onClick={fetchRatings}
                    disabled={loading}
                    className={`rounded-lg px-5 py-2 text-sm font-medium transition-colors ${
                      loading ? 'cursor-not-allowed bg-gray-300 text-gray-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    } `}
                  >
                    {loading ? 'Loading...' : 'Apply Filter'}
                  </button>
                </div>

                <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-center">
                  <div className="relative w-full sm:w-[280px] xl:w-[320px]">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <IoSearch className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search ratings..."
                      value={searchText}
                      onChange={e => handleSearch(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Escape') {
                          onClearInput();
                        }
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-10 pr-10 text-sm text-gray-700 placeholder-gray-400 outline-none transition-all focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500"
                    />
                    {searchText && (
                      <button
                        onClick={onClearInput}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      >
                        <IoClose className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <div className="whitespace-nowrap rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700">
                    Total: <span className="font-bold text-indigo-700">{filteredList.length}</span>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="w-full overflow-x-auto pb-4">
                <GeneralCustomTable
                  tableHeadList={columns}
                  tableDataList={filteredList}
                  allowCheckBox={false}
                  rowKey={record => record.rating_ref_no}
                  loading={loading}
                  emptyText="No ratings found for the selected range"
                  pagination={{ pageSize: 15 }}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
